import { useState, type ComponentProps, type ReactNode } from "react"
import { Ban, TriangleAlert } from "lucide-react"

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogMedia,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Field, FieldLabel } from "@/components/ui/field"
import { Textarea } from "@/components/ui/textarea"
import { useReadOnly } from "@/store/session"

const READ_ONLY_TITLE = "Суб'єкт виключений з реєстру — доступний лише перегляд"

/**
 * design-system.md's `AnnulDialog` row-level trigger (`documents.md`'s own
 * usage; the fuller `details.md` instance additionally composes
 * `KepKeyReader` to sign the annulment — that KEP-signing sub-flow and its
 * real backend submission are out of scope for this pass, per this task's
 * "no backend integration yet" instruction, so this implements only what
 * documents.md itself documents: the destructive confirm trigger, gated by
 * `disabled`/read-only, plus the reason field `Textarea`'s own row
 * ("Annul — 'Причина ануляції'") ties to this dialog.
 */
export function AnnulDialog({
  disabled = false,
  documentNumber,
  onConfirm,
  trigger,
  triggerLabel = "Анулювати",
  triggerSize,
  triggerVariant = "destructive",
}: {
  /** Own prop this component accepts independent of read-only mode — documents.md's row list never passes it, so only `readOnly` applies from that call site. */
  disabled?: boolean
  documentNumber: string
  onConfirm: (reason: string) => void | Promise<void>
  /** Optional, no default — replaces the default `Button` trigger outright (design-system.md's documented `AnnulDialog` contract). Omitting it leaves every existing consumer (`DocumentsList.tsx`) byte-for-byte unchanged; warehouse.md's «Використані» row action passes a `DropdownMenuItem` so the trigger lives inside its own dropdown menu instead of a bare button. The caller owns the custom trigger's own disabled/title styling — this component only wires the `AlertDialogTrigger`'s `onClick` onto it via `asChild`. */
  trigger?: ReactNode
  triggerLabel?: string
  triggerSize?: ComponentProps<typeof Button>["size"]
  triggerVariant?: ComponentProps<typeof Button>["variant"]
}) {
  const readOnly = useReadOnly()
  const isDisabled = disabled || readOnly
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function handleOpenChange(nextOpen: boolean) {
    if (nextOpen) setError(null)
    if (!nextOpen) setReason("")
    setOpen(nextOpen)
  }

  async function handleConfirm() {
    if (submitting || !reason.trim()) return
    setSubmitting(true)
    setError(null)
    try {
      await onConfirm(reason.trim())
      handleOpenChange(false)
    } catch {
      setError("Не вдалося анулювати документ. Спробуйте ще раз.")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AlertDialog onOpenChange={handleOpenChange} open={open}>
      <AlertDialogTrigger asChild>
        {trigger ?? (
          <Button
            disabled={isDisabled}
            size={triggerSize}
            title={readOnly ? READ_ONLY_TITLE : undefined}
            variant={triggerVariant}
          >
            <Ban data-icon="inline-start" />
            <span className="max-lg:sr-only">{triggerLabel}</span>
          </Button>
        )}
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogMedia className="bg-destructive/10 text-destructive">
            <TriangleAlert />
          </AlertDialogMedia>
          <AlertDialogTitle>Анулювати документ</AlertDialogTitle>
          <AlertDialogDescription>
            Документ {documentNumber} буде анульовано.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <Field>
          <FieldLabel htmlFor="annul-reason">Причина ануляції</FieldLabel>
          <Textarea
            id="annul-reason"
            onChange={(event) => setReason(event.target.value)}
            value={reason}
          />
        </Field>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={submitting}>Назад</AlertDialogCancel>
          <Button
            disabled={submitting || !reason.trim()}
            onClick={handleConfirm}
            variant="destructive"
          >
            <Ban data-icon="inline-start" />
            {submitting ? "Анулювання…" : "Анулювати"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
